import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { categories, navigate, showToast, friendlyError, loadCategories } from '../store.js';
import { __, _n } from '../i18n.js';
import { formatMinutes, deadlineChipLabel } from '../lib/format.js';
import { categoryIconName } from '../lib/labels.js';
import { projectStatusInfo } from '../lib/projects.js';
import Header from '../components/Header.jsx';
import Icon from '../components/Icon.jsx';

function resolveCategory(categoryId) {
  return categories.value.find((c) => c.id === categoryId) || null;
}

/**
 * HH-8: Projecten row, reusing the same `.etm-task-list-item` shape every
 * other module's list row uses (ListsScreen/SubscriptionsScreen). The
 * status chip is the only per-row signal — importance/impact/priority live
 * on the detail screen, not the list (plan §4.5's own "the list itself is
 * honest about what matters" is served by the sort order the backend
 * already applies, not by cramming three numbers onto every row).
 */
function ProjectRow({ project }) {
  const status = projectStatusInfo(project.status);
  return (
    <div
      class="etm-task-list-item"
      role="button"
      tabIndex={0}
      aria-label={project.title}
      onClick={() => navigate('project', { projectId: project.id })}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('project', { projectId: project.id });
        }
      }}
    >
      <div class="etm-task-list-item__body">
        <div class="etm-task-list-item__title">
          {project.title}
          {project.is_shared ? <span class="etm-field-hint"> · {__('gedeeld', 'etm')}</span> : null}
        </div>
        <div class="etm-task-list-item__meta">
          {project.open_task_count > 0
            ? _n('%d open taak', '%d open taken', project.open_task_count, 'etm').replace('%d', project.open_task_count)
            : __('Geen open taken', 'etm')}
        </div>
      </div>
      <span class="etm-chip">
        <Icon name={status.icon} size={14} /> {status.label()}
      </span>
    </div>
  );
}

export default function ManageScreen() {
  // HH-8: "Projecten segment under the Taken tab" (plan §3/brief) — a
  // segmented view toggle inside this screen rather than a sixth bottom-nav
  // tab (the MD3 five-tab ceiling HH-1 already locked in), same pattern
  // SubscriptionsScreen's monthly/yearly toggle uses.
  const [view, setView] = useState('tasks');
  const [tab, setTab] = useState('active');
  const [filterCategoryId, setFilterCategoryId] = useState(null);
  const [filterProjectId, setFilterProjectId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (categories.value.length === 0) {
      loadCategories().catch((err) => showToast(friendlyError(err), 'error'));
    }
  }, []);

  // Fetched once regardless of `view`: the project filter chips (tasks
  // view) and the Projecten list (projects view) both need it, and it's
  // cheap enough not to gate behind the toggle — same "one small extra
  // request" tradeoff TaskFormScreen's own project fetch below makes.
  useEffect(() => {
    let cancelled = false;
    api.projects
      .list()
      .then((res) => {
        if (!cancelled) setProjects(res.projects || []);
      })
      .catch((err) => {
        if (!cancelled) showToast(friendlyError(err), 'error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (view !== 'tasks') return undefined;
    let cancelled = false;
    setLoading(true);
    api.tasks
      .list({ status: tab, category_id: filterCategoryId || undefined, project_id: filterProjectId || undefined })
      .then((res) => {
        if (!cancelled) setTasks(res.tasks || []);
      })
      .catch((err) => {
        if (!cancelled) showToast(friendlyError(err), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, tab, filterCategoryId, filterProjectId]);

  const emptyMessage =
    tab === 'active'
      ? __('Geen actieve taken. Voeg er een toe met de +-knop rechtsonder.', 'etm')
      : __('Nog geen voltooide taken.', 'etm');

  return (
    <div>
      {/* WP-7 (brief 7c): the header "+" button is gone — the FAB is now
          the sole add-task affordance on this screen, matching Home. */}
      <Header title={__('Taken', 'etm')} />

      <div class="etm-toggle-row">
        <button type="button" class={view === 'tasks' ? 'active' : ''} onClick={() => setView('tasks')}>
          {__('Taken', 'etm')}
        </button>
        <button type="button" class={view === 'projects' ? 'active' : ''} onClick={() => setView('projects')}>
          {__('Projecten', 'etm')}
        </button>
      </div>

      {view === 'tasks' ? (
        <>
          <div class="etm-tabs">
            <button
              type="button"
              class={`etm-tab-btn${tab === 'active' ? ' active' : ''}`}
              onClick={() => setTab('active')}
            >
              {__('Actief', 'etm')}
            </button>
            <button
              type="button"
              class={`etm-tab-btn${tab === 'completed' ? ' active' : ''}`}
              onClick={() => setTab('completed')}
            >
              {__('Voltooid', 'etm')}
            </button>
          </div>

          <div class="etm-filters">
            <button
              type="button"
              class={`etm-filter-btn${filterCategoryId === null ? ' active' : ''}`}
              onClick={() => setFilterCategoryId(null)}
            >
              {__('Alles', 'etm')}
            </button>
            {categories.value.map((cat) => (
              <button
                type="button"
                key={cat.id}
                class={`etm-filter-btn${filterCategoryId === cat.id ? ' active' : ''}`}
                onClick={() => setFilterCategoryId(cat.id)}
              >
                <Icon name={categoryIconName(cat.icon)} size={18} /> {cat.name}
              </button>
            ))}
          </div>

          {projects.length > 0 ? (
            <div class="etm-filters">
              <button
                type="button"
                class={`etm-filter-btn${filterProjectId === null ? ' active' : ''}`}
                onClick={() => setFilterProjectId(null)}
              >
                {__('Alle projecten', 'etm')}
              </button>
              {projects.map((project) => (
                <button
                  type="button"
                  key={project.id}
                  class={`etm-filter-btn${filterProjectId === project.id ? ' active' : ''}`}
                  onClick={() => setFilterProjectId(project.id)}
                >
                  {project.title}
                </button>
              ))}
            </div>
          ) : null}

          {tasks.length === 0 && !loading ? (
            <div class="etm-empty-state">
              <span class="icon">
                <Icon name={tab === 'active' ? 'checklist' : 'check_circle'} size={44} />
              </span>
              {emptyMessage}
            </div>
          ) : (
            <div class="etm-task-list">
              {tasks.map((task) => {
                const cat = resolveCategory(task.category_id);
                const metaParts = [];
                if (cat) metaParts.push(cat.name);
                metaParts.push(formatMinutes(task.effort_minutes));
                const deadlineLabel = task.deadline ? deadlineChipLabel(task.deadline) : null;

                return (
                  <div
                    key={task.id}
                    class="etm-task-list-item"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate('taskForm', { task })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate('taskForm', { task });
                      }
                    }}
                  >
                    <div class="etm-task-list-item__body">
                      <div class="etm-task-list-item__title">{task.title}</div>
                      <div class="etm-task-list-item__meta">
                        <span>{metaParts.join(' · ')}</span>
                        {task.reward ? <Icon name="redeem" size={14} /> : null}
                        {deadlineLabel ? (
                          <span class="etm-chip">
                            <Icon name="calendar_month" size={14} /> {deadlineLabel}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          {projects.length === 0 ? (
            <div class="etm-empty-state">
              <span class="icon">
                <Icon name="folder" size={44} />
              </span>
              {__('Nog geen projecten. Een project is de plek voor klussen die te groot zijn voor één taak.', 'etm')}
            </div>
          ) : (
            <div class="etm-task-list">
              {projects.map((project) => (
                <ProjectRow project={project} key={project.id} />
              ))}
            </div>
          )}

          <button type="button" class="etm-btn etm-btn-primary" onClick={() => navigate('projectForm', {})}>
            <Icon name="add" size={20} /> {__('Nieuw project', 'etm')}
          </button>
        </>
      )}
    </div>
  );
}
